import { useMemo } from 'react';
import { usePrestationSubsidyRules } from '@/api/prestations/prestations.queries';
import type { PrestationDetail } from '@/api/prestations/prestations.types';
import { Table } from '@/components/table/Table';
import { useTableState } from '@/components/table/useTableState';
import { buildSubsidyRuleColumns } from '@/features/prestation-rules/columns/subsidy-rules-columns';
import { formatSubsidyProductLabel } from './SubsidyTargetCombobox';

const columns = buildSubsidyRuleColumns(undefined);

interface PrestationSubsidyRulesProps {
  prestation: Pick<
    PrestationDetail,
    'id' | 'category' | 'ticketingProductVariantCode' | 'ticketingProductLabel'
  >;
}

export function PrestationSubsidyRules({ prestation }: PrestationSubsidyRulesProps) {
  const { data: rules, isLoading, isError } = usePrestationSubsidyRules(prestation.id);
  const tableData = useMemo(() => rules ?? [], [rules]);
  const tableState = useTableState('prestation-subsidy-rules', { columns });

  const target = prestation.ticketingProductVariantCode
    ? formatSubsidyProductLabel(
        prestation.ticketingProductLabel ?? prestation.ticketingProductVariantCode
      )
    : prestation.category?.path;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-medium">Règles de subvention</h3>
        {target && (
          <p className="text-sm text-gray-500">
            Règles de la billetterie qui s'appliquent à « {target} ».
          </p>
        )}
      </div>
      <Table
        entity={{ singular: 'règle', feminine: true }}
        data={tableData}
        columns={columns}
        tableState={tableState}
        isLoading={isLoading}
        isError={isError}
        disableHover
        emptyState={
          rules?.length === 0 ? (
            <p className="p-4 text-gray-500">
              Aucune règle de subvention ne s'applique : la part CE sera nulle.
            </p>
          ) : undefined
        }
      />
    </div>
  );
}
